import React, { useState, useEffect } from "react";
import Input from "~/components/Input";
import Select from "~/components/Select";
import ImageUpload from "~/components/ImageUpload";
import { convertFilesToImages } from "~/utils/converters";
import { useUploadProductNoteImage } from "~/data/upload";

type SelectProps = {
    options?: { name: string; label: string }[];
    value?: string;
    [x: string]: any;
};
export const SelectOption: React.FC<SelectProps> = ({
    options = [],
    placeholder = "Please select value",
    maxLength,
    value,
    onChange,
    label,
    ...otherProps
}: SelectProps) => {
    return (
        <div className="form-group">
            <Select
                value={value ?? ""}
                options={
                    options?.map((item) => ({ label: item.label, value: item.name })) ??
                    []
                }
                {...otherProps}
                defaultValue={""}
                onChange={(valueSelect) => onChange(valueSelect)}
            ></Select>
        </div>
    );
};
type TextProps = {
    [x: string]: any;
};
export const TextOption: React.FC<TextProps> = ({
    placeholder = "Please enter value",
    label,
    maxlength,
    type,
    onChange,
    ...otherProps
}: TextProps) => {
    return (
        <div className="form-group ">
            <Input
                className="form-control"
                placeholder={placeholder}
                maxLength={maxlength}
                type={type}
                {...otherProps}
                onChange={(e) => onChange(e?.target?.value)}
            />
            <p className="text-gray-400">
                {" "}
                {maxlength && <span>Max {maxlength} characters.</span>}
            </p>
        </div>
    );
};

export const RadioOption: React.FC<TextProps> = () => {
    return (
        <div className="form-group">
            <Input className="form-control" type="number" />
        </div>
    );
};

export const UploadImageOption: React.FC<TextProps> = ({
    value,
    onChange,
    error,
    placeholder = "Upload an image",
    maxFiles = 1,
    ...otherProps
}: TextProps) => {
    const [uploadedFiles, setUploadedFiles] = useState<IFile[]>(() => {
        // Initialize with existing value if it's a URL string
        if (value && typeof value === "string") {
            return [
                {
                    name: "uploaded-image",
                    size: 0,
                    type: "image/*",
                    url: value,
                    file: new File([], "uploaded-image"),
                },
            ];
        }
        return [];
    });
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);    // Use the GraphQL mutation hook
    const [uploadProductNoteImage, { loading: isUploading }] = useUploadProductNoteImage({
        onCompleted: (data) => {
            if (data.uploadProductNoteImage.success && data.uploadProductNoteImage.url) {
                // Update the uploaded files with the server URL
                setUploadedFiles((prev) =>
                    prev.map((file, index) => ({
                        ...file,
                        url: data.uploadProductNoteImage.url,
                        name: file.name || `uploaded-image-${index}`,
                    }))
                );

                // Call onChange with the server URL
                onChange(data.uploadProductNoteImage.url);
            } else {
                throw new Error(data.uploadProductNoteImage.message || "Upload failed");
            }
        },
        onError: (error) => {
            console.error("Upload error:", error);
            setUploadError(error.message || "Failed to upload image");

            // Remove the failed upload from displayed files
            setUploadedFiles([]);
            onChange("");
        },
    });

    // Update uploadedFiles when value prop changes
    useEffect(() => {
        if (value && typeof value === "string" && uploadedFiles.length === 0) {
            setUploadedFiles([
                {
                    name: "uploaded-image",
                    size: 0,
                    type: "image/*",
                    url: value,
                    file: new File([], "uploaded-image"),
                },
            ]);
        } else if (!value && uploadedFiles.length > 0) {
            setUploadedFiles([]);
        }
    }, [value]);

    const handleFileChange = (files: File[]) => {
        setUploadError(null);
        const convertedFiles = convertFilesToImages(files);
        const newFiles = convertedFiles.slice(0, maxFiles);
        setUploadedFiles(newFiles);
    }; const handleBase64Change = async (
        base64Data: { base64: string; fileName: string }[]
    ) => {
        if (base64Data.length === 0) return;

        setUploadError(null);

        try {
            const firstFile = base64Data[0];

            // Update uploaded files to show the filename
            setUploadedFiles((prev) =>
                prev.map((file) => ({
                    ...file,
                    name: firstFile.fileName,
                }))
            );

            await uploadProductNoteImage({
                variables: {
                    input: {
                        base64_image: firstFile.base64,
                        filename: firstFile.fileName,
                    },
                },
            });
        } catch (error: any) {
            console.error("Upload error:", error);
            setUploadError(error.message || "Failed to upload image");

            // Remove the failed upload from displayed files
            setUploadedFiles([]);
            onChange("");
        }
    };
    const handleFileRemove = (fileToRemove: IFile) => {
        const filteredFiles = uploadedFiles.filter(
            (file) => file.url !== fileToRemove.url
        );
        setUploadedFiles(filteredFiles);

        if (filteredFiles.length > 0) {
            onChange(filteredFiles[0].url);
        } else {
            onChange("");
        }

        // Clear upload error when removing files
        setUploadError(null);

        // Clean up object URL
        URL.revokeObjectURL(fileToRemove.url);
    };

    return (
        <div className="form-group">
            <div className="flex flex-col gap-3">
                {uploadedFiles.length > 0 ? (
                    <div className="space-y-3">
                        {" "}
                        <div className="flex flex-wrap gap-2">
                            {uploadedFiles.map((file) => (
                                <div key={file.url} className="relative group">
                                    <div className="relative overflow-hidden rounded border border-gray-200 hover:border-blue-300 transition-colors">
                                        <img
                                            src={file.url}
                                            alt={file.name}
                                            className="w-16 h-16 object-cover transition-transform group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200" />
                                        <div
                                            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                            onClick={() => setPreviewImage(file.url)}
                                        >
                                            <div className="bg-white bg-opacity-90 rounded-full p-1 hover:bg-opacity-100 transition-all">
                                                <svg
                                                    className="w-3 h-3 text-gray-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleFileRemove(file)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-red-600 shadow-sm transition-colors"
                                        title="Remove image"
                                    >
                                        ×
                                    </button>
                                    <div className="mt-1 text-xs text-gray-500 truncate max-w-16">
                                        {file.name}
                                    </div>
                                </div>
                            ))}{" "}
                            {uploadedFiles.length < maxFiles && (
                                <ImageUpload
                                    onFileChange={handleFileChange}
                                    onBase64Change={handleBase64Change}
                                    multiple={maxFiles > 1}
                                    returnBase64={true}
                                    disabled={isUploading}
                                    containerStyle={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 4,
                                        border: "1px dashed #d1d5db",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: isUploading ? "#f3f4f6" : "#f9fafb",
                                        transition: "all 0.2s ease",
                                        opacity: isUploading ? 0.7 : 1,
                                    }}
                                    className={`hover:border-blue-400 hover:bg-blue-50 ${isUploading ? "cursor-not-allowed" : ""}`}
                                >
                                    {isUploading ? (
                                        <div className="flex flex-col items-center gap-1">
                                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                            <span className="text-xs text-gray-500">
                                                Uploading...
                                            </span>
                                        </div>
                                    ) : null}
                                </ImageUpload>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="border border-dashed border-gray-300 rounded p-4 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <ImageUpload
                            onFileChange={handleFileChange}
                            onBase64Change={handleBase64Change}
                            multiple={maxFiles > 1}
                            returnBase64={true}
                            disabled={isUploading}
                            containerStyle={{
                                width: "100%",
                                minHeight: 80,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                opacity: isUploading ? 0.7 : 1,
                            }}
                            className={isUploading ? "cursor-not-allowed" : ""}
                        >
                            {isUploading ? (
                                <div className="flex flex-col items-center gap-3">
                                    <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                    <div>
                                        <p className="text-sm font-medium text-blue-600">
                                            Uploading image...
                                        </p>
                                        <p className="text-xs text-gray-500">Please wait</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg
                                            className="w-4 h-4 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700">
                                            {placeholder}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Click to browse{maxFiles > 1 ? ` (Max ${maxFiles})` : ""}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </ImageUpload>
                    </div>
                )}
                {/* Image Preview Modal */}
                {previewImage && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                        onClick={() => setPreviewImage(null)}
                    >
                        <div className="relative max-w-4xl max-h-full">
                            <img
                                src={previewImage}
                                alt="Preview"
                                className="max-w-full max-h-full object-contain rounded-lg"
                                onClick={(e) => e.stopPropagation()}
                            />
                            <button
                                onClick={() => setPreviewImage(null)}
                                className="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 rounded-full w-10 h-10 flex items-center justify-center transition-all"
                                title="Close preview"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}{" "}
                {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        {error}
                    </div>
                )}
                {uploadError && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        {uploadError}
                    </div>
                )}
            </div>
        </div>
    );
};

export type ElementOptionsCustomization = {
    Select: typeof SelectOption;
    Text: typeof TextOption;
    Radio: typeof RadioOption;
    UploadImage: typeof UploadImageOption;
};

const ElementOptions: ElementOptionsCustomization = {
    Select: SelectOption,
    Text: TextOption,
    Radio: RadioOption,
    UploadImage: UploadImageOption,
};

export default ElementOptions;
